import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { consequentialNote, demoEntryNote, type Solution } from '../../data/solutionCatalog';
import { demoUrlFor } from '../../data/demoCenter';
import { fadeUp } from '../../utils/motion';
import { ImpactPanel } from './ImpactPanel';
type SolutionBodyProps = {
  solution: Solution;
  /** Use h3 headings when the solution sits inside another page section. */
  nested?: boolean;
  /** Solution-specific sections, placed after the modules and before the demo invitation. */
  children?: React.ReactNode;
};
const assessmentSolutions: Solution['id'][] = ['government', 'education', 'talent', 'leadership', 'community'];

/**
 * Shared solution structure after the introduction:
 * tasks → potential impact → who uses it → workflow → modules → try the workspace → discuss implementation.
 */
export function SolutionBody({
  solution,
  nested = false,
  children
}: SolutionBodyProps) {
  const Heading = nested ? 'h3' : 'h2';
  const SubHeading = nested ? 'h4' : 'h3';
  const prefix = solution.id;
  return <>
      <section aria-labelledby={`${prefix}-tasks-title`} className="bg-white border-b border-line py-16 lg:py-20" data-id="element-2623" data-mp="2n8jyamli8tcz">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16" data-id="element-2624" data-mp="2s7dg2r7tr6iy">
          <Heading id={`${prefix}-tasks-title`} className="lg:col-span-5 font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-2625" data-mp="2w7x85gdjjjy8">
            {solution.tasksHeading}
          </Heading>
          <ul className={`lg:col-span-7 grid grid-cols-1 gap-x-10 border-t border-line ${solution.tasks.length > 5 ? 'sm:grid-cols-2' : ''}`} data-id="element-2626" data-mp="3ayebj59qkjxr">
            {solution.tasks.map(task => <li key={task} className="flex gap-3 border-b border-line py-3.5 text-lg text-ink-dark leading-snug" data-id="element-2627" data-mp="3k173laqqszon">
                <span aria-hidden="true" className="mt-3 h-0.5 w-3 shrink-0 bg-teal" data-id="element-2628" data-mp="3owmf3w1a2jhw" />
                {task}
              </li>)}
          </ul>
        </motion.div>
      </section>

      <ImpactPanel solution={solution} headingLevel={Heading} data-id="element-2629" data-mp="1kwp20i2hxhvz" />

      <section aria-labelledby={`${prefix}-workspaces-title`} className="bg-white border-b border-line py-16 lg:py-20" data-id="element-2630" data-mp="1ynaoshv2mbz5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-2631" data-mp="1ttziqiy31z8a">
          <motion.div {...fadeUp} className="lg:col-span-5" data-id="element-2632" data-mp="28mjh76aafije">
            <Heading id={`${prefix}-workspaces-title`} className="font-heading font-bold text-3xl text-ink-dark tracking-tight leading-tight" data-id="element-2633" data-mp="23kamo5hro8f6">
              Who uses it
            </Heading>
            <dl className="mt-8 border-t border-line" data-id="element-2634" data-mp="2cmtocm9tgezu">
              {solution.workspaces.map(workspace => <div key={workspace.name} className="border-b border-line py-5" data-id="element-2635" data-mp="n8x4to3fugoe">
                  <dt className="font-heading font-semibold text-lg text-ink-dark" data-id="element-2636" data-mp="i9tym0bq24eu">{workspace.name}</dt>
                  <dd className="mt-1 text-ink-muted leading-relaxed" data-id="element-2637" data-mp="2txfmuzcqkpr8">{workspace.role}</dd>
                </div>)}
            </dl>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7" data-id="element-2638" data-mp="2l29dxri65rkh">
            <SubHeading className="font-heading font-bold text-xl text-ink-dark" data-id="element-2639" data-mp="2p4e4uih8ga3f">A representative workflow</SubHeading>
            <ol className="mt-6 space-y-0" data-id="element-2640" data-mp="2b4lh8w1069zo">
              {solution.workflow.map((step, index) => <li key={step} className="relative flex gap-4 pb-6 last:pb-0" data-id="element-2641" data-mp="2g47tp390bm7f" data-mpi={index}>
                  {index < solution.workflow.length - 1 && <span aria-hidden="true" className="absolute left-[0.9375rem] top-8 bottom-0 w-px bg-line" data-id="element-2642" data-mp="2269j88xt49x7" />}
                  <span className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line bg-white font-heading text-sm font-bold text-teal" data-id="element-2643" data-mp="d38g6t1vlu7">
                    {index + 1}
                  </span>
                  <span className="pt-1 font-heading font-semibold text-lg text-ink-dark" data-id="element-2644" data-mp="3bru60aqczhl3">{step}</span>
                </li>)}
            </ol>

            <div className="mt-10 border-t border-line pt-6" data-id="element-2645" data-mp="31upedvdqutww">
              <SubHeading className="text-sm font-semibold text-ink-dark" data-id="element-2646" data-mp="31t4mkditdp2q">Relevant modules</SubHeading>
              <ul className="mt-3 flex flex-wrap gap-2" data-id="element-2647" data-mp="2wxz09dli28e4">
                {solution.modules.map(module => <li key={module} className="rounded border border-line px-2.5 py-1 text-sm font-medium text-navy" data-id="element-2648" data-mp="2sw41oguye941">
                    {module}
                  </li>)}
              </ul>
              <a href="/how-it-works#modules" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-navy hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-2649" data-mp="2j254zxdnfhjh">
                How modules work <ArrowRight size={14} aria-hidden="true" data-id="element-2650" data-mp="2558btpa25nzf" />
              </a>
            </div>

            {solution.developmentScope && <div className="mt-8 border-l-2 border-teal pl-5" data-id="element-2651" data-mp="oo9ahg173evd">
                <p className="text-sm font-semibold text-teal" data-id="element-2652" data-mp="omg3u0ql0und">Under development</p>
                <p className="mt-1 text-ink-dark leading-relaxed" data-id="element-2653" data-mp="toni4wyvwmsv">{solution.developmentScope}</p>
              </div>}
            {assessmentSolutions.includes(solution.id) && <p className="mt-6 text-sm text-ink-muted leading-relaxed" data-id="element-2654" data-mp="zzuiuqnxlc5">{consequentialNote}</p>}
          </motion.div>
        </div>
      </section>

      {children}

      <section aria-labelledby={`${prefix}-try-title`} className="bg-navy-deep text-white py-16 lg:py-20" data-id="element-2655" data-mp="awcq5y5rq99j">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end" data-id="element-2656" data-mp="fnur4m7sk9ui">
          <div className="lg:col-span-7" data-id="element-2657" data-mp="3ebgz1lnqpjx4">
            <Heading id={`${prefix}-try-title`} className="font-heading font-bold text-3xl lg:text-4xl tracking-tight leading-tight" data-id="element-2658" data-mp="3jc4ssf3csev1">
              {solution.demoLabel}
            </Heading>
            <p className="mt-4 text-lg text-white/80 leading-relaxed" data-id="element-2659" data-mp="37awokxndvl7i">
              Open the {solution.workspaces.map(workspace => workspace.name).join(' and ')} and follow the workflow
              yourself. {demoEntryNote}
            </p>
            <a href={demoUrlFor(solution.id)} target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-2660" data-mp="3g38d2dyxsyku">
              {solution.demoLabel}
              <ArrowUpRight size={18} aria-hidden="true" data-id="element-2661" data-mp="2xbfl8mvltjep" />
              <span className="sr-only" data-id="element-2662" data-mp="hkt04y5fkheo">(opens in a new tab)</span>
            </a>
          </div>
          <div className="lg:col-span-5 border-t border-white/20 pt-8 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10" data-id="element-2663" data-mp="cm8bzx3gdo6l">
            <SubHeading className="font-heading font-bold text-xl" data-id="element-2664" data-mp="1uj3bewjxwoht">Discuss implementation</SubHeading>
            <p className="mt-2 text-white/75 leading-relaxed" data-id="element-2665" data-mp="1ziy2p17mng3e">
              Tell us which workflows you want to improve and we’ll scope a focused implementation.
            </p>
            <a href="/contact" className="mt-5 inline-flex items-center gap-2 font-semibold text-white hover:text-teal transition-colors duration-150 whitespace-nowrap" data-id="element-2666" data-mp="1qhq4sk90wh9u">
              Contact SyNeural <ArrowRight size={16} aria-hidden="true" data-id="element-2667" data-mp="2i84ctzucuzdl" />
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}